import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { LoadingOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import { zodResolver } from "@hookform/resolvers/zod";
import { App } from "antd5";
import { z } from "zod";

import Button from "components/ui/button/Button";
import { useCreateOpportunity } from "../../../lib/hooks/api/opportunities/useCreateOpportunity";
import { useOpportunityWorkflow } from "../../../lib/hooks/api/opportunities/useOpportunityWorkflow";
import { routes } from "../../../lib/routes";
import { Flex } from "../../../styles/utility-components";
import CompanyUserSelect from "../../form_components/CompanyUserSelect";
import { DatePicker, Input } from "../../form_components/Inputs";
import { OpportunityStageDropdownSelect } from "../opportunity_stage/OpportunityStageDropdownSelect";
import { ActionMessageContent } from "../saving/ActionMessageContent";
import { opportunitySchema } from "../utils";
import { BuyerMultiSelect } from "./BuyersMultiSelect";
import { getDefaultStageId } from "./utils";

import css from "./CreateOpportunityForm.module.scss";

export type CreateOpportunityFormValues = z.infer<typeof opportunitySchema> & {
  stage?: string;
  noticeStage?: string;
};

type Props = {
  defaultValues?: Partial<CreateOpportunityFormValues>;
  hiddenFields?: (keyof CreateOpportunityFormValues)[];
  onComplete?: () => void;
  navigateToNewOpportunity?: boolean;
};

export function CreateOpportunityForm({
  defaultValues,
  hiddenFields,
  onComplete,
  navigateToNewOpportunity,
}: Props) {
  const { message } = App.useApp();
  const { data: workflow } = useOpportunityWorkflow({ enabled: !!defaultValues?.stageId });
  const { formState, control, handleSubmit, reset, setValue, watch } =
    useForm<CreateOpportunityFormValues>({
      defaultValues,
      resolver: zodResolver(opportunitySchema),
    });

  const { mutate: createOpportunity, isLoading } = useCreateOpportunity({
    onSuccess: (data) => {
      reset();
      if (navigateToNewOpportunity) {
        window.location.href = `${routes.opportunities}/${data.createOpportunity.id}`;
        return;
      }
      onComplete?.();
      message.open({
        icon: null,
        content: (
          <ActionMessageContent
            message="Opportunity created successfully"
            objectType="opportunity"
            url={`${routes.opportunities}/${data.createOpportunity.id}`}
          />
        ),
      });
    },
    onError: () => message.error("Failed to create opportunity"),
  });

  const stageId = watch("stageId");

  useEffect(() => {
    if (workflow && !stageId) {
      const defaultStage = getDefaultStageId(workflow, defaultValues);

      if (defaultStage) {
        setValue("stageId", defaultStage, { shouldValidate: true });
      }
    }
  }, [workflow, stageId, setValue, defaultValues]);

  return (
    <Form
      onSubmit={handleSubmit(({ entities, ...opportunity }) =>
        createOpportunity({ opportunity, entities: { entities: entities || [] } }),
      )}
    >
      <Flex flexDirection="column" gap={24}>
        <Input
          name="name"
          control={control}
          label="Name"
          required
          placeholder="Name this opportunity"
        />
        <BuyerMultiSelect
          defaultEntities={defaultValues?.entities}
          setValue={setValue}
          placeholder="Select"
        />
        <CompanyUserSelect name="assignedToId" control={control} label="Assigned To" />
        {!hiddenFields?.includes("value") && (
          <Input
            name="value"
            control={control}
            label="Value"
            type="number"
            placeholder="Add a value"
          />
        )}
        {!hiddenFields?.includes("closeDate") && (
          <DatePicker
            name="closeDate"
            control={control}
            label="Close Date"
            placeholder="Add a close date"
          />
        )}
        <OpportunityStageDropdownSelect
          name="stageId"
          control={control}
          label="Stage"
          placeholder="Select a stage"
          className={css.stageSelect}
          required
        />
      </Flex>

      <Flex column gap={8} flexGrow={1} justifyContent="flex-end">
        <Button buttonType="primary" type="submit" disabled={!formState.isValid || isLoading}>
          {isLoading && <LoadingOutlined />}Create opportunity
        </Button>
      </Flex>
    </Form>
  );
}

const Form = styled.form({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  flexGrow: 1,
  gap: 24,
});
